import React, { useEffect } from "react";

import { CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { FLOW } from "../../Container/AccountPlanContainer/flowCopy";
import { useMyPlanActivity } from "../../Hooks/useAccountPlan";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchAccountPlansEnabled } from "../../Store/Reducers/AccountPlanSlice";
import type { PlanActivityFinished } from "../../Types/AccountPlan";
import type { PlanActivityMineItem } from "../../Types/AccountPlan";
import { stateChip } from "../../Utils/AccountPlans/queueText";

/** Signed-in routes live under /<org slug>/… (App.tsx); the tray sits inside that route. */
export const orgPath = (slug: string | undefined, path: string) =>
  slug ? `/${slug}${path}` : path;

const TOAST_TEXT: Record<
  PlanActivityFinished["event"],
  {
    title: (v: string) => string;
    body: string;
    ok: boolean;
    /** The button and where it lands; default "Open plan" on the plan page. */
    open?: { label: string; query: string };
  }
> = {
  // Confirm plan (Phase 2): every read upload waits on the AE before any briefing.
  ready: {
    title: () => "your upload is ready to confirm",
    body: "Check the slides we read. The briefing is written only after you confirm.",
    ok: true,
    open: { label: "Take action", query: "?tab=viewer" }, // Confirm plan
  },
  ready_partial: {
    // Read outcomes come before confirm, so never carry a number (D1).
    title: () => "your upload is ready to confirm, with gaps",
    body: "Some parts couldn't be produced. Check what we read, then confirm it.",
    ok: true,
    open: { label: "Take action", query: "?tab=viewer" }, // Confirm plan
  },
  read_failed: {
    title: () => "couldn't read your upload",
    body: "Open the plan to see why and try again.",
    ok: false,
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  rejected: {
    title: () => "your upload needs review",
    body: "A plan check failed. Open it to see why.",
    ok: false,
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  needs_review: {
    title: () => "your upload needs review",
    body: "Fix the deck and upload it again, or confirm it's right. No briefing is written until you do.",
    ok: false,
    // Sushant, 2026-10-31: the AE's step, named like the Plans list's button.
    open: { label: "Take action", query: "?tab=plan&focus=problem" },
  },
  briefing_ready: {
    title: v =>
      v ? `draft briefing for plan ${v} is ready` : "draft briefing is ready",
    body: "Review it, then send each section to its owner to verify. Ask about this plan opens once it is published.",
    ok: true,
    // Sushant, 2026-10-31: the news is the briefing, so the button opens it.
    open: { label: "Open briefing", query: "?tab=sponsor" },
  },
  briefing_failed: {
    title: () => "the draft briefing couldn't be written",
    body: "The plan is still available. Open it and try again.",
    ok: false,
    open: { label: "Open briefing", query: "?tab=sponsor" },
  },
};

/**
 * Everywhere in ExecBridge (mounted once, next to the bell): a chip while any of my
 * plans is in line or processing, and a pop-up when one of them lands (spec NTF-05).
 * The bell keeps the same news for later; this is for people who are online now.
 */
/**
 * The pill's words (D40, Sushant 2026-10-31): the step, with no queue number or time
 * estimate, as the Plans list says it. Stalled and other states keep stateChip.
 */
export const pillLabel = (item: PlanActivityMineItem): string => {
  const stalled = item.queue?.read?.stalled || item.queue?.brief?.stalled;
  if (!stalled) {
    if (item.state === "in_line") return FLOW.queuedForParsing;
    if (item.state === "reading") return FLOW.parsing;
    if (item.state === "brief_in_line") return FLOW.briefingQueued;
    if (item.state === "briefing")
      return item.plan_version != null
        ? `${FLOW.writingBriefingFor} v${item.plan_version}`
        : FLOW.writingBriefingFor.replace(/ for$/, "");
  }
  return stateChip(item.state, item.queue).label;
};

const PlanActivityTray: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const enabled = useAppSelector(s => s.accountPlan.enabled);
  useEffect(() => {
    if (enabled === null) void dispatch(fetchAccountPlansEnabled());
  }, [enabled, dispatch]);
  const { active, toasts, dismiss } = useMyPlanActivity(enabled === true);

  if (enabled !== true || (!active.length && !toasts.length)) return null;
  const first = active[0];
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[1100] flex w-[calc(100%-32px)] max-w-sm flex-col items-end gap-2"
      aria-live="polite"
    >
      {toasts.slice(-3).map(t => {
        const text = TOAST_TEXT[t.event];
        // The plan number only (D1); `version` is the internal upload number.
        const version = t.plan_version != null ? `v${t.plan_version}` : "";
        return (
          <div
            key={`${t.file_id}:${t.event}`}
            role="status"
            data-testid="plan-toast"
            className="pointer-events-auto grid w-full grid-cols-[auto_1fr_auto] items-start gap-2.5 rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
          >
            {text.ok ? (
              <CheckCircle2
                className="mt-0.5 h-4 w-4 text-emerald-600"
                aria-hidden
              />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 text-rose-600" aria-hidden />
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">
                {t.account_name || "Your account"}: {text.title(version)}
              </div>
              <div className="text-xs text-gray-600">{text.body}</div>
              <button
                type="button"
                onClick={() => {
                  dismiss(t.file_id, t.event);
                  navigate(
                    orgPath(
                      orgSlug,
                      `/account-plan/${t.account_id}${text.open?.query ?? ""}`
                    )
                  );
                }}
                className="mt-1.5 rounded-md bg-[#974ae4] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
              >
                {text.open?.label ?? "Open plan"}
              </button>
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(t.file_id, t.event)}
              className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        );
      })}
      {first && (
        <button
          type="button"
          data-testid="plan-tray-chip"
          // The plan page exists for every role that uploads (vendor admins have no
          // My Accounts, so the Activity view isn't a safe target for everyone).
          onClick={() =>
            navigate(
              orgPath(
                orgSlug,
                // One plan: reopen its progress dialog (PlanVersionsCard).
                active.length === 1
                  ? `/account-plan/${first.account_id}?tab=plan&progress=${first.file_id}`
                  : `/account-plan/${first.account_id}?tab=plan`
              )
            )
          }
          title={pillLabel(first)}
          className="pointer-events-auto inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 shadow-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <Loader2
            className="h-3.5 w-3.5 shrink-0 animate-spin text-[#974ae4]"
            aria-hidden
          />
          <span className="truncate">
            {active.length === 1
              ? `${first.account_name || "Plan"} · ${pillLabel(first)}`
              : `${active.length} plans processing`}
          </span>
        </button>
      )}
    </div>
  );
};

export default PlanActivityTray;
